import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { API_BASE } from '../lib/api'
import '../styles/ui.css'

type Classification = { id: string; name: string }

type InboxItem = {
    id: string
    storeId: string
    storeName: string
    requestMessage: string | null
    status: 'Pending' | 'Approved' | 'Rejected'
    requestedAt: string
    reviewedAt: string | null
    grantedClassification: string | null
    rejectionReason: string | null
    user: {
        id: string
        displayName: string
        email: string
        avatarUrl: string | null
    }
}

export default function AccessInboxPage() {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [filter, setFilter] = useState<'Pending' | 'All'>('Pending')
    const [approveModal, setApproveModal] = useState<InboxItem | null>(null)
    const [rejectModal, setRejectModal] = useState<InboxItem | null>(null)
    const [classificationId, setClassificationId] = useState('')
    const [rejectReason, setRejectReason] = useState('')

    const authHeaders = { Authorization: `Bearer ${token}` }

    const { data: requests = [], isLoading } = useQuery<InboxItem[]>({
        queryKey: ['access-inbox', filter],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/access-requests/inbox', { headers: authHeaders })
            const data: InboxItem[] = await res.json()
            return filter === 'Pending' ? data.filter(r => r.status === 'Pending') : data
        },
        staleTime: 15_000,
        refetchInterval: 30_000  // Auto-refresh every 30s
    })

    const { data: classifications = [] } = useQuery<Classification[]>({
        queryKey: ['classifications-brief'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/classifications', { headers: authHeaders })
            const data = await res.json()
            return data.classifications
        }
    })

    const approve = useMutation({
        mutationFn: async ({ id, classId }: { id: string; classId: string }) => {
            const res = await fetch(API_BASE + `/api/admin/access-requests/${id}/approve`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ classificationId: classId })
            })
            if (!res.ok) throw new Error((await res.json()).error ?? 'Failed')
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['access-inbox'] })
            qc.invalidateQueries({ queryKey: ['access-inbox-pending-count'] })
            setApproveModal(null)
            setClassificationId('')
        }
    })

    const reject = useMutation({
        mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
            const res = await fetch(API_BASE + `/api/admin/access-requests/${id}/reject`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason })
            })
            if (!res.ok) throw new Error((await res.json()).error ?? 'Failed')
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['access-inbox'] })
            qc.invalidateQueries({ queryKey: ['access-inbox-pending-count'] })
            setRejectModal(null)
            setRejectReason('')
        }
    })

    const pendingCount = requests.filter(r => r.status === 'Pending').length

    return (
        <>
            <div className="page-header">
                <h1>Access Inbox</h1>
                <p>Review and approve store access requests from your staff.</p>
            </div>

            <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                    <h3>
                        Requests
                        {pendingCount > 0 && filter === 'All' && (
                            <span className="nav-badge" style={{ marginLeft: 8 }}>{pendingCount} pending</span>
                        )}
                    </h3>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <button
                            className={`btn ${filter === 'Pending' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setFilter('Pending')}
                        >
                            Pending
                        </button>
                        <button
                            className={`btn ${filter === 'All' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setFilter('All')}
                        >
                            All
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="empty-state"><p>Loading…</p></div>
                ) : requests.length === 0 ? (
                    <div className="empty-state">
                        <div className="icon">📥</div>
                        <h3>{filter === 'Pending' ? 'No pending requests' : 'No requests yet'}</h3>
                        <p>Staff members will appear here when they request store access.</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Store</th>
                                <th>Message</th>
                                <th>Requested</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map(req => (
                                <tr key={req.id}>
                                    <td>
                                        <div className="user-cell">
                                            <UserAvatar user={req.user} />
                                            <div className="user-cell-info">
                                                <div className="name">{req.user.displayName}</div>
                                                <div className="email">{req.user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ fontWeight: 500 }}>{req.storeName}</td>
                                    <td style={{
                                        maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', color: 'var(--color-text-secondary)',
                                        fontSize: 'var(--text-xs)'
                                    }}>
                                        {req.requestMessage ?? <em style={{ opacity: 0.5 }}>No message</em>}
                                    </td>
                                    <td style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                                        {new Date(req.requestedAt).toLocaleDateString()}
                                    </td>
                                    <td>
                                        <div>
                                            <span className={`badge badge-${req.status.toLowerCase()}`}>{req.status}</span>
                                            {req.grantedClassification && (
                                                <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                                                    {req.grantedClassification}
                                                </div>
                                            )}
                                            {req.rejectionReason && (
                                                <div style={{ fontSize: '11px', color: 'var(--color-error)', marginTop: 2 }} title={req.rejectionReason}>
                                                    {req.rejectionReason.length > 30
                                                        ? req.rejectionReason.slice(0, 30) + '…'
                                                        : req.rejectionReason}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {req.status === 'Pending' && (
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon success"
                                                    title="Approve"
                                                    onClick={() => {
                                                        setApproveModal(req)
                                                        setClassificationId(classifications[0]?.id ?? '')
                                                    }}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    className="btn-icon danger"
                                                    title="Reject"
                                                    onClick={() => setRejectModal(req)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Approve Modal */}
            {approveModal && (
                <div className="modal-backdrop">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Approve Access</h2>
                            <button className="modal-close" onClick={() => setApproveModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                Approving <strong style={{ color: 'var(--color-text-primary)' }}>{approveModal.user.displayName}</strong> for{' '}
                                <strong style={{ color: 'var(--color-text-primary)' }}>{approveModal.storeName}</strong>
                            </div>
                            {approveModal.requestMessage && (
                                <div style={{
                                    background: 'var(--color-surface)',
                                    borderRadius: 'var(--radius-md)',
                                    padding: 'var(--space-3)',
                                    marginBottom: 'var(--space-4)',
                                    fontSize: 'var(--text-sm)',
                                    color: 'var(--color-text-secondary)',
                                    fontStyle: 'italic'
                                }}>
                                    "{approveModal.requestMessage}"
                                </div>
                            )}
                            <div className="form-field">
                                <label className="form-label">Staff Classification</label>
                                <select
                                    className="form-select"
                                    value={classificationId}
                                    onChange={e => setClassificationId(e.target.value)}
                                >
                                    {classifications.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            {approve.isError && (
                                <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>
                                    {String((approve.error as Error).message)}
                                </p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setApproveModal(null)}>Cancel</button>
                            <button
                                className="btn btn-success"
                                disabled={!classificationId || approve.isPending}
                                onClick={() => approve.mutate({ id: approveModal.id, classId: classificationId })}
                            >
                                {approve.isPending ? 'Approving…' : '✓ Approve Access'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {rejectModal && (
                <div className="modal-backdrop">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Reject Request</h2>
                            <button className="modal-close" onClick={() => setRejectModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div style={{ marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                                Rejecting <strong style={{ color: 'var(--color-text-primary)' }}>{rejectModal.user.displayName}</strong>'s request for{' '}
                                <strong style={{ color: 'var(--color-text-primary)' }}>{rejectModal.storeName}</strong>
                            </div>
                            <div className="form-field">
                                <label className="form-label">Reason (shown to user)</label>
                                <textarea
                                    className="form-textarea"
                                    placeholder="Explain why this request is being rejected…"
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button>
                            <button
                                className="btn btn-danger"
                                disabled={reject.isPending}
                                onClick={() => reject.mutate({ id: rejectModal.id, reason: rejectReason })}
                            >
                                {reject.isPending ? 'Rejecting…' : 'Reject Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

function UserAvatar({ user }: { user: { displayName: string; avatarUrl: string | null } }) {
    const initials = user.displayName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    return user.avatarUrl ? (
        <img src={user.avatarUrl} alt={user.displayName} className="user-avatar" referrerPolicy="no-referrer" />
    ) : (
        <div className="user-avatar-fallback">{initials}</div>
    )
}
